import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Trash2, Plus, MessageSquare, Eye, BarChart3, Edit, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuestionsPage = () => {
  const [questions, setQuestions] = useState([]);
  const [formData, setFormData] = useState({
    text: '',
    question_type: 'text',
    scale_min: 1,
    scale_max: 10
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [editFormData, setEditFormData] = useState({
    id: '',
    text: '',
    question_type: 'text',
    scale_min: 1,
    scale_max: 10
  });

  const questionTemplates = [
    {
      category: 'Pupil Self-Reflection',
      icon: '💭',
      templates: [
        { text: 'How confident did you feel during today\'s lesson?', question_type: 'scale', scale_min: 1, scale_max: 10 },
        { text: 'What was the most challenging part of today\'s lesson?', question_type: 'text' },
        { text: 'What went well today that you\'re proud of?', question_type: 'text' },
        { text: 'Is there anything you\'d like to spend more time on next lesson?', question_type: 'text' },
      ]
    },
    {
      category: 'Instructor Assessment',
      icon: '📋',
      templates: [
        { text: 'How well did the pupil respond to feedback today?', question_type: 'scale', scale_min: 1, scale_max: 10 },
        { text: 'Were there any recurring errors or habits that need addressing?', question_type: 'text' },
        { text: 'Did the pupil demonstrate improvement from the previous lesson?', question_type: 'text' },
        { text: 'How would you rate their hazard awareness today?', question_type: 'scale', scale_min: 1, scale_max: 10 },
      ]
    },
    {
      category: 'Lesson Structure & Planning',
      icon: '📝',
      templates: [
        { text: 'Were the lesson objectives achieved today?', question_type: 'text' },
        { text: 'What should be the focus for the next lesson?', question_type: 'text' },
        { text: 'Is the pupil on track for their test date?', question_type: 'text' },
      ]
    },
    {
      category: 'Safety & Confidence',
      icon: '🛡',
      templates: [
        { text: 'Did you need to use dual controls at any point?', question_type: 'text' },
        { text: 'How would you rate the pupil\'s independent decision-making?', question_type: 'scale', scale_min: 1, scale_max: 10 },
        { text: 'Were there any near-miss situations to discuss?', question_type: 'text' },
      ]
    },
    {
      category: 'Test Readiness',
      icon: '🎯',
      templates: [
        { text: 'How would you rate their test readiness today?', question_type: 'scale', scale_min: 1, scale_max: 10 },
        { text: 'Which test manoeuvres still need practice?', question_type: 'text' },
        { text: 'Can the pupil follow sat nav directions independently?', question_type: 'text' },
      ]
    }
  ];

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questions`);
      setQuestions(response.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('Failed to load questions');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.text.trim()) {
      toast.error('Question text is required');
      return;
    }

    if (formData.question_type === 'scale' && (formData.scale_min >= formData.scale_max)) {
      toast.error('Scale minimum must be less than maximum');
      return;
    }

    try {
      await axios.post(`${API}/questions`, formData);
      toast.success('Question added successfully');
      setFormData({
        text: '',
        question_type: 'text',
        scale_min: 1,
        scale_max: 10
      });
      setIsDialogOpen(false);
      fetchQuestions();
    } catch (error) {
      console.error('Error adding question:', error);
      toast.error('Failed to add question');
    }
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await axios.delete(`${API}/questions/${questionId}`);
      toast.success('Question deleted successfully');
      fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question');
    }
  };

  const handleMove = async (index, direction) => {
    const newQuestions = [...questions];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newQuestions.length) return;
    [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
    setQuestions(newQuestions);
    try {
      await axios.put(`${API}/questions/reorder`, {
        question_ids: newQuestions.map(q => q.id)
      });
    } catch (error) {
      toast.error('Failed to reorder');
      fetchQuestions();
    }
  };

  const handleEdit = (question) => {
    setEditFormData({
      id: question.id,
      text: question.text,
      question_type: question.question_type,
      scale_min: question.scale_min || 1,
      scale_max: question.scale_max || 10
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData.text.trim()) {
      toast.error('Question text is required');
      return;
    }

    if (editFormData.question_type === 'scale' && (editFormData.scale_min >= editFormData.scale_max)) {
      toast.error('Scale minimum must be less than maximum');
      return;
    }

    try {
      const updateData = {
        text: editFormData.text,
        question_type: editFormData.question_type,
        scale_min: editFormData.scale_min,
        scale_max: editFormData.scale_max
      };
      
      await axios.put(`${API}/questions/${editFormData.id}`, updateData);
      toast.success('Question updated successfully');
      setEditFormData({
        id: '',
        text: '',
        question_type: 'text',
        scale_min: 1,
        scale_max: 10
      });
      setIsEditDialogOpen(false);
      fetchQuestions();
    } catch (error) {
      console.error('Error updating question:', error);
      toast.error('Failed to update question');
    }
  };

  const addTemplateQuestion = async (template) => {
    // Check if question already exists
    const exists = questions.some(q => q.text.toLowerCase() === template.text.toLowerCase());
    if (exists) {
      toast.error('This question already exists');
      return;
    }

    try {
      const data = {
        text: template.text,
        question_type: template.question_type,
        scale_min: template.scale_min || 1,
        scale_max: template.scale_max || 10
      };
      await axios.post(`${API}/questions`, data);
      toast.success('Question added from template');
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to add question');
    }
  };

  const addAllFromCategory = async (templates) => {
    let added = 0;
    for (const template of templates) {
      const exists = questions.some(q => q.text.toLowerCase() === template.text.toLowerCase());
      if (exists) continue;
      try {
        const data = {
          text: template.text,
          question_type: template.question_type,
          scale_min: template.scale_min || 1,
          scale_max: template.scale_max || 10
        };
        await axios.post(`${API}/questions`, data);
        added++;
      } catch (error) { console.error('Failed to add template question:', error); }
    }
    if (added > 0) {
      toast.success(`Added ${added} question${added > 1 ? 's' : ''}`);
      fetchQuestions();
    } else {
      toast.info('All questions from this category already exist');
    }
  };

  const getQuestionTypeIcon = (type) => {
    return type === 'scale' ? <BarChart3 size={16} /> : <MessageSquare size={16} />;
  };

  const getQuestionTypeBadge = (question) => {
    if (question.question_type === 'scale') {
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          Scale {question.scale_min}-{question.scale_max}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
        Text Response
      </Badge>
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200">Questions</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">Create and manage reflection questions for your pupils</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2" size={16} />
              Add New Question
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Question</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="question">Question Text *</Label>
                <Textarea
                  id="question"
                  value={formData.text}
                  onChange={(e) => setFormData({...formData, text: e.target.value})}
                  placeholder="Enter your reflection question..."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="question_type">Question Type *</Label>
                <select
                  id="question_type"
                  value={formData.question_type}
                  onChange={(e) => setFormData({...formData, question_type: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="text">Text Response</option>
                  <option value="scale">Scale Rating</option>
                </select>
              </div>

              {formData.question_type === 'scale' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="scale_min">Scale Minimum</Label>
                    <Input
                      id="scale_min"
                      type="number"
                      value={formData.scale_min}
                      onChange={(e) => setFormData({...formData, scale_min: parseInt(e.target.value)})}
                      min="1"
                      max="9"
                    />
                  </div>
                  <div>
                    <Label htmlFor="scale_max">Scale Maximum</Label>
                    <Input
                      id="scale_max"
                      type="number"
                      value={formData.scale_max}
                      onChange={(e) => setFormData({...formData, scale_max: parseInt(e.target.value)})}
                      min="2"
                      max="10"
                    />
                  </div>
                </div>
              )}

              <Button type="submit" className="w-full">Add Question</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Question Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Question</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <Label htmlFor="edit-question">Question Text *</Label>
                <Textarea
                  id="edit-question"
                  value={editFormData.text}
                  onChange={(e) => setEditFormData({...editFormData, text: e.target.value})}
                  placeholder="Enter your reflection question..."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="edit-question_type">Question Type *</Label>
                <select
                  id="edit-question_type"
                  value={editFormData.question_type}
                  onChange={(e) => setEditFormData({...editFormData, question_type: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="text">Text Response</option>
                  <option value="scale">Scale Rating</option>
                </select>
              </div>

              {editFormData.question_type === 'scale' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-scale_min">Scale Minimum</Label>
                    <Input
                      id="edit-scale_min"
                      type="number"
                      value={editFormData.scale_min}
                      onChange={(e) => setEditFormData({...editFormData, scale_min: parseInt(e.target.value)})}
                      min="1"
                      max="9"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-scale_max">Scale Maximum</Label>
                    <Input
                      id="edit-scale_max"
                      type="number"
                      value={editFormData.scale_max}
                      onChange={(e) => setEditFormData({...editFormData, scale_max: parseInt(e.target.value)})}
                      min="2"
                      max="10"
                    />
                  </div>
                </div>
              )}

              <Button type="submit" className="w-full">Update Question</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Question Template Library */}
      <Card className="mb-6">
        <CardHeader className="cursor-pointer py-4" onClick={() => setShowTemplates(!showTemplates)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg text-slate-800">Question Template Library</CardTitle>
              <Badge variant="secondary" className="text-xs">Quick Add</Badge>
            </div>
            <Eye size={18} className={`text-green-600 transition-transform ${showTemplates ? 'rotate-180' : ''}`} />
          </div>
        </CardHeader>
        {showTemplates && (
          <CardContent className="pt-0 space-y-4">
            <p className="text-sm text-gray-500 mb-2">Browse recommended questions by category. Click to add individually or add all from a category.</p>
            {questionTemplates.map((cat) => {
              const existingTexts = questions.map(q => q.text.toLowerCase());
              return (
                <div key={cat.category} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between bg-gray-50 px-4 py-3">
                    <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                      <span>{cat.icon}</span> {cat.category}
                    </h4>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`add-all-${cat.category.replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => addAllFromCategory(cat.templates)}
                      className="text-xs"
                    >
                      Add All ({cat.templates.filter(t => !existingTexts.includes(t.text.toLowerCase())).length})
                    </Button>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {cat.templates.map((template, idx) => {
                      const alreadyAdded = existingTexts.includes(template.text.toLowerCase());
                      return (
                        <div key={template.text} className="flex items-center justify-between px-4 py-3">
                          <div className="flex-1 pr-4">
                            <p className={`text-sm ${alreadyAdded ? 'text-green-600' : 'text-gray-700'}`}>{template.text}</p>
                            <Badge variant="outline" className="text-xs mt-1">
                              {template.question_type === 'scale' ? `Scale ${template.scale_min || 1}-${template.scale_max || 10}` : 'Text Response'}
                            </Badge>
                          </div>
                          {alreadyAdded ? (
                            <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Added</Badge>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid={`add-template-${idx}-${cat.category.replace(/\s+/g, '-').toLowerCase()}`}
                              onClick={() => addTemplateQuestion(template)}
                              className="text-xs"
                            >
                              <Plus size={14} className="mr-1" /> Add
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </CardContent>
        )}
      </Card>

      <div className="space-y-4">
        {questions.map((question, index) => (
          <Card key={question.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  {/* Reorder arrows */}
                  <div className="flex flex-col gap-0.5 pt-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={index === 0}
                      onClick={() => handleMove(index, -1)}
                      data-testid={`move-up-${question.id}`}
                      className="h-7 w-7 p-0 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-30"
                    >
                      <ChevronUp size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={index === questions.length - 1}
                      onClick={() => handleMove(index, 1)}
                      data-testid={`move-down-${question.id}`}
                      className="h-7 w-7 p-0 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-30"
                    >
                      <ChevronDown size={16} />
                    </Button>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <Badge variant="secondary">Q{index + 1}</Badge>
                      {getQuestionTypeBadge(question)}
                      <span className="text-xs text-gray-500">
                        Created: {new Date(question.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="mt-1">
                        {getQuestionTypeIcon(question.question_type)}
                      </div>
                      <div className="flex-1">
                        <p className="text-lg text-gray-800 mb-2">{question.text}</p>
                        {question.question_type === 'scale' && (
                          <p className="text-sm text-gray-600">
                            Scale range: {question.scale_min} (lowest) to {question.scale_max} (highest)
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(question)}
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(question.id)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {questions.length === 0 && (
        <Card className="text-center p-8">
          <CardContent>
            <MessageSquare size={48} className="mx-auto text-green-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No questions yet</h3>
            <p className="text-gray-500">Add your first reflection question to get started</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};


export default QuestionsPage;
